import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from '../../../util/reactIntl';
import { Modal, Button } from '../../../components';
import DragAndDrop from '../../../components/DragAndDrop/DragAndDrop';
import { UploadImage, DeleteMedia } from '../../../util/cloudinary';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import {
  STORE_COVER_ACCEPT_STRING,
  STORE_COVER_ASPECT_RATIO,
  STORE_COVER_HEIGHT,
  STORE_COVER_MAX_FILE_SIZE_BYTES,
  STORE_COVER_MIN_HEIGHT,
  STORE_COVER_MIN_WIDTH,
  STORE_COVER_RECOMMENDED_SIZE,
  STORE_COVER_WIDTH,
  STORE_COVER_MIN_SIZE,
} from './storeCoverConstants';
import css from './CustomizeStoreCoverModal.module.css';

const TAB_UPLOAD = 'upload';
const TAB_COLOR = 'color';
const COVER_MODE_IMAGE = 'image';
const COVER_MODE_COLOR = 'color';

const hexToRgb = hex => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null;
};

const hexToHsl = hex => {
  const rgb = hexToRgb(hex);
  if (!rgb) return { h: 270, s: 100, l: 60 };
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const hslToHex = (h, s, l) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const DEFAULT_HEX = '#9751F2';
const ASPECT_RATIO_WARNING_TOLERANCE = 0.02;

const getImageDimensions = file =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(objectUrl);
      resolve(dimensions);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to read image dimensions.'));
    };
    img.src = objectUrl;
  });

const CustomizeStoreCoverModal = ({
  isOpen,
  onClose,
  onSaveStoreCover,
  onManageDisableScrolling,
  storeCoverImage,
  storeCoverBackgroundColor,
  storeDisplayPhoto,
  storeName,
  intl,
}) => {
  const [activeTab, setActiveTab] = useState(TAB_UPLOAD);
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingColor, setPendingColor] = useState(storeCoverBackgroundColor || DEFAULT_HEX);
  const [alpha, setAlpha] = useState(100);
  const [uploadError, setUploadError] = useState('');
  const [uploadWarnings, setUploadWarnings] = useState([]);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saveError, setSaveError] = useState('');
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [pendingUploadedPublicId, setPendingUploadedPublicId] = useState(null);
  const [coverMode, setCoverMode] = useState(storeCoverImage?.secure_url ? COVER_MODE_IMAGE : COVER_MODE_COLOR);
  const [switchConfirmation, setSwitchConfirmation] = useState('');
  const fileInputRef = useRef(null);
  const slAreaRef = useRef(null);
  const currentUploadTokenRef = useRef(0);
  const previewUrlRef = useRef('');

  const initialColor = storeCoverBackgroundColor || DEFAULT_HEX;
  const currentImageUrl =
    coverMode === COVER_MODE_IMAGE
      ? localPreviewUrl || pendingImage?.secure_url || storeCoverImage?.secure_url
      : '';
  const currentColor = pendingColor || initialColor;
  const rgb = hexToRgb(currentColor);
  const userChangedColor = pendingColor !== initialColor;
  const hasStoredImage = !!storeCoverImage?.secure_url;
  const hasStoredColor = !!storeCoverBackgroundColor;
  const hasEffectiveImage = !!(pendingImage?.secure_url || storeCoverImage?.secure_url);
  const hasChanges =
    coverMode === COVER_MODE_IMAGE
      ? !!pendingImage || (!hasStoredImage && !!localPreviewUrl) || (hasStoredColor && hasEffectiveImage)
      : !!userChangedColor || hasStoredImage || (!hasStoredColor && !!pendingColor);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const cleanupPendingUpload = useCallback(async () => {
    if (!pendingUploadedPublicId) return;
    try {
      await DeleteMedia({ publicId: pendingUploadedPublicId, resourceType: 'image' });
    } catch (err) {
      // Non-blocking cleanup: we do not prevent closing when Cloudinary cleanup fails.
      console.error('Failed to clean up unapplied cover upload:', err);
    } finally {
      setPendingUploadedPublicId(null);
    }
  }, [pendingUploadedPublicId]);

  const resetLocalPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setLocalPreviewUrl('');
  }, []);

  const requireSwitchConfirmation = useCallback(
    nextMode => {
      if (nextMode === COVER_MODE_COLOR && hasEffectiveImage) {
        setSwitchConfirmation('image-to-color');
        return true;
      }
      if (nextMode === COVER_MODE_IMAGE && (userChangedColor || hasStoredColor)) {
        setSwitchConfirmation('color-to-image');
        return true;
      }
      return false;
    },
    [hasEffectiveImage, userChangedColor, hasStoredColor]
  );

  const validateFile = useCallback(
    file => {
      setUploadError('');
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      if (!['.jpg', '.jpeg', '.png', '.webp'].includes(ext)) {
        setUploadError(
          intl.formatMessage({
            id: 'CustomizeStoreCoverModal.invalidType',
            defaultMessage: 'Please upload a JPG, PNG, or WEBP image.',
          })
        );
        return false;
      }
      if (file.size > STORE_COVER_MAX_FILE_SIZE_BYTES) {
        setUploadError(
          intl.formatMessage({
            id: 'CustomizeStoreCoverModal.fileTooLarge',
            defaultMessage: 'File must be 5MB or smaller.',
          })
        );
        return false;
      }
      return true;
    },
    [intl]
  );

  const getDimensionWarnings = useCallback(
    ({ width, height }) => {
      const warnings = [];
      if (!width || !height) return warnings;

      const imageAspectRatio = width / height;
      const hasAspectRatioMismatch =
        Math.abs(imageAspectRatio - STORE_COVER_ASPECT_RATIO) > ASPECT_RATIO_WARNING_TOLERANCE;
      const hasLowResolution = width < STORE_COVER_MIN_WIDTH || height < STORE_COVER_MIN_HEIGHT;

      if (hasAspectRatioMismatch) {
        warnings.push(
          intl.formatMessage(
            {
              id: 'CustomizeStoreCoverModal.aspectRatioWarning',
              defaultMessage:
                'This image ratio is {actualRatio}. Your header works best at {recommendedRatio}, so edges may be cropped.',
            },
            {
              actualRatio: `${width}:${height}`,
              recommendedRatio: `${STORE_COVER_WIDTH}:${STORE_COVER_HEIGHT}`,
            }
          )
        );
      }

      if (hasLowResolution) {
        warnings.push(
          intl.formatMessage(
            {
              id: 'CustomizeStoreCoverModal.lowResolutionWarning',
              defaultMessage:
                'This image is smaller than the recommended minimum of {minWidth} × {minHeight} and may look blurry.',
            },
            {
              minWidth: STORE_COVER_MIN_WIDTH,
              minHeight: STORE_COVER_MIN_HEIGHT,
            }
          )
        );
      }

      return warnings;
    },
    [intl]
  );

  const handleFileSelect = useCallback(
    async files => {
      const file = files && files[0];
      if (!file) return;
      // if (requireSwitchConfirmation(COVER_MODE_IMAGE)) return;
      if (!validateFile(file)) return;
      setUploadWarnings([]);
      const uploadToken = currentUploadTokenRef.current + 1;
      currentUploadTokenRef.current = uploadToken;

      if (pendingUploadedPublicId) {
        await cleanupPendingUpload();
      }

      resetLocalPreview();
      const nextPreview = URL.createObjectURL(file);
      previewUrlRef.current = nextPreview;
      setLocalPreviewUrl(nextPreview);
      setCoverMode(COVER_MODE_IMAGE);
      setPendingImage(null);
      setUploadInProgress(true);
      setUploadProgress(0);
      setUploadError('');
      setSwitchConfirmation('');
      try {
        const dimensions = await getImageDimensions(file);
        if (currentUploadTokenRef.current !== uploadToken) {
          return;
        }
        setUploadWarnings(getDimensionWarnings(dimensions));

        const result = await UploadImage({
          file,
          id: uuidv4(),
          folder: 'store owners',
          onProgress: progress => {
            if (currentUploadTokenRef.current !== uploadToken) return;
            setUploadProgress(progress);
          },
        });
        if (currentUploadTokenRef.current !== uploadToken) {
          if (result?.public_id) {
            await DeleteMedia({ publicId: result.public_id, resourceType: 'image' });
          }
          return;
        }
        setPendingImage({
          public_id:  result.public_id,
          secure_url: result.secure_url,
          width: dimensions.width,
          height: dimensions.height,
        });
        setPendingUploadedPublicId(result.public_id);
        setUploadProgress(100);
      } catch (err) {
        setUploadError(
          err.message || intl.formatMessage({ id: 'CustomizeStoreCoverModal.uploadFailed', defaultMessage: 'Upload failed. Please try again.' })
        );
        setUploadWarnings([]);
        resetLocalPreview();
      } finally {
        if (currentUploadTokenRef.current === uploadToken) {
          setUploadInProgress(false);
        }
      }
    },
    [validateFile, intl, pendingUploadedPublicId, cleanupPendingUpload, resetLocalPreview, requireSwitchConfirmation, hasStoredColor, getDimensionWarnings]
  );

  const handleDrop = useCallback(
    files => {
      handleFileSelect(files);
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    e => {
      const files = e.target.files;
      if (files && files.length) handleFileSelect(Array.from(files));
      e.target.value = '';
    },
    [handleFileSelect]
  );

  const handleHexChange = useCallback(
    e => {
      // if (requireSwitchConfirmation(COVER_MODE_COLOR)) return;
      const v = e.target.value;
      setCoverMode(COVER_MODE_COLOR);
      setSwitchConfirmation('');
      if (v.startsWith('#')) setPendingColor(v);
      else if (/^[0-9A-Fa-f]{0,6}$/.test(v)) setPendingColor(v ? '#' + v : DEFAULT_HEX);
    },
    [requireSwitchConfirmation]
  );

  const handleEyedropper = useCallback(async () => {
    // if (requireSwitchConfirmation(COVER_MODE_COLOR)) return;
    if (!window.EyeDropper) return;
    try {
      const dropper = new window.EyeDropper();
      const { sRGBHex } = await dropper.open();
      setCoverMode(COVER_MODE_COLOR);
      setSwitchConfirmation('');
      setPendingColor(sRGBHex || DEFAULT_HEX);
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('EyeDropper failed:', err);
    }
  }, [requireSwitchConfirmation]);

  const hsl = hexToHsl(currentColor);

  const handleHueChange = useCallback(e => {
    // if (requireSwitchConfirmation(COVER_MODE_COLOR)) return;
    const h = parseInt(e.target.value, 10);
    setCoverMode(COVER_MODE_COLOR);
    setSwitchConfirmation('');
    setPendingColor(hslToHex(h, hsl.s, hsl.l));
  }, [hsl.s, hsl.l, requireSwitchConfirmation]);

  const handleSlChange = useCallback((e, isDrag = false) => {
    // if (requireSwitchConfirmation(COVER_MODE_COLOR)) return;
    const rect = slAreaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height));
    const s = Math.round(x * 100);
    const l = Math.round(y * 100);
    setCoverMode(COVER_MODE_COLOR);
    setSwitchConfirmation('');
    setPendingColor(hslToHex(hsl.h, s, l));
  }, [hsl.h, requireSwitchConfirmation]);

  const handleSlMouseDown = useCallback(
    e => {
      handleSlChange(e);
      const onMove = ev => handleSlChange(ev, true);
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [handleSlChange]
  );

  const handleAlphaChange = useCallback(e => {
    setAlpha(parseInt(e.target.value, 10));
  }, []);

  const handleCancel = useCallback(async () => {
    currentUploadTokenRef.current += 1;
    await cleanupPendingUpload();
    resetLocalPreview();
    setUploadError('');
    setSaveError('');
    setPendingImage(null);
    setPendingColor(storeCoverBackgroundColor || DEFAULT_HEX);
    setCoverMode(storeCoverImage?.secure_url ? COVER_MODE_IMAGE : COVER_MODE_COLOR);
    setSwitchConfirmation('');
    setUploadWarnings([]);
    setUploadProgress(0);
    setAlpha(100);
    onClose();
  }, [onClose, storeCoverBackgroundColor, storeCoverImage, cleanupPendingUpload, resetLocalPreview]);

  const handleApply = useCallback(async () => {
    if (uploadInProgress) return;
    setSaveError('');
    setSaveInProgress(true);
    try {
      const payload = {};
      if (coverMode === COVER_MODE_IMAGE) {
        payload.storeCoverBackgroundColor = null;
        if (pendingImage) {
          payload.storeCoverImage = pendingImage;
        } else if (!storeCoverImage?.secure_url) {
          setSaveInProgress(false);
          return;
        }
      } else {
        payload.storeCoverImage = null;
        payload.storeCoverBackgroundColor = pendingColor;
      }
      if (Object.keys(payload).length === 0) {
        setSaveInProgress(false);
        return;
      }
      await onSaveStoreCover(payload);
      setPendingUploadedPublicId(null);
      resetLocalPreview();
      setPendingImage(null);
      setPendingColor(initialColor);
      setCoverMode(payload.storeCoverImage ? COVER_MODE_IMAGE : COVER_MODE_COLOR);
      setUploadWarnings([]);
      onClose();
    } catch (err) {
      setSaveError(
        err.message || intl.formatMessage({ id: 'CustomizeStoreCoverModal.saveFailed', defaultMessage: 'Failed to save. Please try again.' })
      );
    } finally {
      setSaveInProgress(false);
    }
  }, [uploadInProgress, coverMode, pendingImage, pendingColor, initialColor, onSaveStoreCover, onClose, intl, storeCoverImage, resetLocalPreview]);

  const confirmSwitchToColor = useCallback(async () => {
    setCoverMode(COVER_MODE_COLOR);
    setActiveTab(TAB_COLOR);
    setSwitchConfirmation('');
    setPendingImage(null);
    setUploadWarnings([]);
    await cleanupPendingUpload();
    resetLocalPreview();
  }, [resetLocalPreview, cleanupPendingUpload]);

  const confirmSwitchToImage = useCallback(() => {
    setCoverMode(COVER_MODE_IMAGE);
    setActiveTab(TAB_UPLOAD);
    setSwitchConfirmation('');
  }, []);

  if (!isOpen) return null;

  return (
    <Modal
      id="CustomizeStoreCoverModal"
      isOpen={isOpen}
      onClose={handleCancel}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    // containerClassName={css.modalContainer}
    // contentClassName={css.modalContent}
    className={css.CustomizeStoreCoverModal}
    >
      <div className={css.root}>
        <div className={css.header}>
          <div className={css.titleRow}>
            <span className={css.titleIcon} aria-hidden>
              <i className="ri-image-add-line" />
            </span>
            <div>
              <h2 className={css.title}>
                <FormattedMessage id="CustomizeStoreCoverModal.title" defaultMessage="Customize Store Cover" />
              </h2>
              <p className={css.subtitle}>
                <FormattedMessage
                  id="CustomizeStoreCoverModal.subtitle"
                  defaultMessage="Upload an image or choose a background color for your store banner."
                />
              </p>
            </div>
          </div>
        </div>

        <div className={css.tabs}>
          <button
            type="button"
            className={classNames(css.tab, activeTab === TAB_UPLOAD && css.tabActive)}
            onClick={() => {
              if (requireSwitchConfirmation(COVER_MODE_IMAGE)) return;
              setSwitchConfirmation('');
              setActiveTab(TAB_UPLOAD);
            }}
            aria-selected={activeTab === TAB_UPLOAD}
          >
            <i className="ri-upload-2-line" aria-hidden />
            <FormattedMessage id="CustomizeStoreCoverModal.uploadImage" defaultMessage="Upload Image" />
          </button>
          <button
            type="button"
            className={classNames(css.tab, activeTab === TAB_COLOR && css.tabActive)}
            onClick={() => {
              if (requireSwitchConfirmation(COVER_MODE_COLOR)) return;
              setSwitchConfirmation('');
              setActiveTab(TAB_COLOR);
            }}
            aria-selected={activeTab === TAB_COLOR}
          >
            <i className="ri-palette-line" aria-hidden />
            <FormattedMessage id="CustomizeStoreCoverModal.backgroundColor" defaultMessage="Background Color" />
          </button>
        </div>

        <div className={css.body}>
          <div key={activeTab} className={css.tabContentArea}>
          {activeTab === TAB_UPLOAD ? (
            <div className={css.tabPane}>
              <DragAndDrop
                handleDrop={handleDrop}
                accept={STORE_COVER_ACCEPT_STRING}
                dragdrop={classNames(css.dropZone, uploadInProgress && css.dropZoneDisabled)}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={STORE_COVER_ACCEPT_STRING}
                  onChange={handleFileInputChange}
                  className={css.fileInput}
                  aria-label={intl.formatMessage({ id: 'CustomizeStoreCoverModal.uploadAria', defaultMessage: 'Upload banner image' })}
                />
                <div
                  className={css.dropZoneInner}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
                >
                  <span className={css.uploadIcon}>
                    <i className="ri-upload-cloud-2-line" aria-hidden />
                  </span>
                  <span className={css.uploadText}><span className={css.uploadTextBold}>
                    <FormattedMessage
                      id="CustomizeStoreCoverModal.clickToUpload"
                      defaultMessage="Click to upload"
                    />&nbsp;
                  </span>
                    <FormattedMessage
                      id="CustomizeStoreCoverModal.orDragAndDrop"
                      defaultMessage="or drag and drop"
                    />
                  </span>
                  <span className={css.dropZoneHint}>
                    JPG, PNG, WEBP (<FormattedMessage id="CustomizeStoreCoverModal.max5MB" defaultMessage="Max 5MB" />)
                  </span>
                </div>
              </DragAndDrop>
              <div className={css.infoBanner}>
                <i className="ri-information-line" aria-hidden />
                <b>
                  <FormattedMessage
                    id="CustomizeStoreCoverModal.recommendedSizeText"
                    defaultMessage="Recommended Banner Size:"
                  />
                </b>
                <FormattedMessage
                  id="CustomizeStoreCoverModal.recommendedSize"
                  defaultMessage="{recommended} pixels (minimum {min} pixels)"
                  values={{
                    recommended: STORE_COVER_RECOMMENDED_SIZE,
                    min: STORE_COVER_MIN_SIZE,
                  }}
                />
              </div>
              <p className={css.guidanceText}>
                <FormattedMessage
                  id="CustomizeStoreCoverModal.croppingBehavior"
                  defaultMessage="Images are centered and shown using cover mode. If the ratio is different, edges may be cropped."
                />
              </p>
              {uploadWarnings.length ? (
                <div className={css.warningBanner}>
                  <i className="ri-alert-line" aria-hidden />
                  <div>
                    {uploadWarnings.map(message => (
                      <p key={message} className={css.warningText}>
                        {message}
                      </p>
                    ))}
                  </div>
                </div>
              ) : null}
              {uploadError ? <p className={css.errorText}>{uploadError}</p> : null}
              {uploadInProgress ? (
                <p className={css.uploadingText}>
                  {intl.formatMessage({ id: 'CustomizeStoreCoverModal.uploading', defaultMessage: 'Uploading…' })} {uploadProgress}%
                </p>
              ) : null}
            </div>
          ) : (
            <div className={css.tabPane}>
              <div className={css.colorSection}>
              <div className={css.colorPickerCard}>
                <div
                  ref={slAreaRef}
                  className={css.slArea}
                  style={{ backgroundColor: `hsl(${hsl.h}, 100%, 50%)` }}
                  onMouseDown={handleSlMouseDown}
                  role="slider"
                  aria-label={intl.formatMessage({ id: 'CustomizeStoreCoverModal.pickColor', defaultMessage: 'Pick color' })}
                  tabIndex={0}
                  onKeyDown={e => {
                    const step = e.shiftKey ? 10 : 2;
                    if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      setPendingColor(hslToHex(hsl.h, Math.min(100, hsl.s + step), hsl.l));
                    } else if (e.key === 'ArrowLeft') {
                      e.preventDefault();
                      setPendingColor(hslToHex(hsl.h, Math.max(0, hsl.s - step), hsl.l));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setPendingColor(hslToHex(hsl.h, hsl.s, Math.min(100, hsl.l + step)));
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setPendingColor(hslToHex(hsl.h, hsl.s, Math.max(0, hsl.l - step)));
                    }
                  }}
                >
                  <div className={css.slOverlay} />
                  <div
                    className={css.slThumb}
                    style={{
                      left: `${hsl.s}%`,
                      top: `${100 - hsl.l}%`,
                    }}
                  />
                </div>
             <div className={css.slidersRowbody}> 
             <div className={css.slidersRow}>
                  {typeof window !== 'undefined' && window.EyeDropper && (
                    <button
                      type="button"
                      className={css.eyedropperBtn}
                      style={{ backgroundColor: currentColor }}
                      onClick={handleEyedropper}
                      aria-label={intl.formatMessage({ id: 'CustomizeStoreCoverModal.eyedropper', defaultMessage: 'Pick color from screen' })}
                    >
                      <i className="ri-dropper-line" aria-hidden />
                    </button>
                  )}
                  <div className={css.slidersColumn}>
                    <div className={css.hueSliderWrap} style={{ '--thumb-color': currentColor }}>
                      <input
                        type="range"
                        min={0}
                        max={360}
                        value={hsl.h}
                        onChange={handleHueChange}
                        className={css.hueSlider}
                        aria-label={intl.formatMessage({ id: 'CustomizeStoreCoverModal.hue', defaultMessage: 'Hue' })}
                      />
                    </div>
                    <div
                      className={css.alphaSliderWrap}
                      style={{ '--alpha-color': currentColor }}
                    >
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={alpha}
                        onChange={handleAlphaChange}
                        className={css.alphaSlider}
                        aria-label={intl.formatMessage({ id: 'CustomizeStoreCoverModal.opacity', defaultMessage: 'Opacity' })}
                      />
                    </div>
                  </div>
                </div>
                <div className={css.colorInputs}>
                  <label className={css.colorLabel}>
                    HEX
                    <input
                      type="text"
                      value={currentColor}
                      onChange={handleHexChange}
                      className={css.hexInput}
                    />
                  </label>
                  {rgb && (
                    <div className={css.rgbGroup}>
                      <div className={css.rgbLabels}>
                        <span>R</span>
                        <span>G</span>
                        <span>B</span>
                      </div>
                      <div className={css.rgbInputs}>
                        <input type="number" readOnly value={rgb.r} className={css.rgbInput} min={0} max={255} aria-label="Red" />
                        <input type="number" readOnly value={rgb.g} className={css.rgbInput} min={0} max={255} aria-label="Green" />
                        <input type="number" readOnly value={rgb.b} className={css.rgbInput} min={0} max={255} aria-label="Blue" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </div>
              </div>
            </div>
          )}
          </div>

          <div className={css.previewSection}>
            <h3 className={css.previewTitle}>
              <FormattedMessage id="CustomizeStoreCoverModal.preview" defaultMessage="Preview" />
            </h3>
            <div
              className={css.previewBox}
              style={{
                '--store-cover-width': STORE_COVER_WIDTH,
                '--store-cover-height': STORE_COVER_HEIGHT,
                backgroundImage: currentImageUrl ? `url(${currentImageUrl})` : undefined,
                backgroundColor: !currentImageUrl
                  ? (rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha / 100})` : currentColor)
                  : undefined,
              }}
            >
              {!currentImageUrl && !currentColor && (
                <span className={css.previewPlaceholder} aria-hidden>
                  <i className="ri-image-line" />
                </span>
              )}
              {storeDisplayPhoto?.secure_url && (
                <div className={css.previewLogo}>
                  <img src={storeDisplayPhoto.secure_url} alt="" />
                </div>
              )}
              {uploadInProgress ? (
                <div className={css.previewLoadingOverlay}>
                  <span className={css.previewLoadingText}>
                    {intl.formatMessage({ id: 'CustomizeStoreCoverModal.uploading', defaultMessage: 'Uploading…' })} {uploadProgress}%
                  </span>
                </div>
              ) : null}
            </div>
            <p className={css.previewHint}>
              <FormattedMessage
                id="CustomizeStoreCoverModal.previewHint"
                defaultMessage="This is how your banner will appear on your store."
              />
            </p>
            {switchConfirmation ? (
              <div className={css.switchConfirmBox}>
                <p className={css.switchConfirmText}>
                  {switchConfirmation === 'image-to-color'
                    ? intl.formatMessage({
                      id: 'CustomizeStoreCoverModal.confirmSwitchToColor',
                      defaultMessage: 'An image is currently selected. Switching to color will replace it when you apply changes.',
                    })
                    : intl.formatMessage({
                      id: 'CustomizeStoreCoverModal.confirmSwitchToImage',
                      defaultMessage: 'A color is currently selected. Uploading an image will replace the color when you apply changes.',
                    })}
                </p>
                <div className={css.switchConfirmActions}>
                  <Button
                    variant="secondary-outline"
                    onClick={() => setSwitchConfirmation('')}
                  >
                    <FormattedMessage id="CustomizeStoreCoverModal.keepCurrent" defaultMessage="Keep current" />
                  </Button>
                  <Button
                    variant="primary"
                    onClick={switchConfirmation === 'image-to-color' ? confirmSwitchToColor : confirmSwitchToImage}
                  >
                    <FormattedMessage id="CustomizeStoreCoverModal.confirmSwitch" defaultMessage="Switch" />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {saveError ? <p className={css.errorText}>{saveError}</p> : null}

        <div className={css.footer}>
          <Button variant={"secondary-outline"} onClick={handleCancel} rootClassName={css.cancelButton}>
            <FormattedMessage id="CustomizeStoreCoverModal.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={handleApply}
            inProgress={saveInProgress}
            disabled={saveInProgress || uploadInProgress || !hasChanges}
            variant={"primary"}
          >
            <FormattedMessage id="CustomizeStoreCoverModal.applyChanges" defaultMessage="Apply Changes" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

CustomizeStoreCoverModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveStoreCover: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  storeCoverImage: PropTypes.object,
  storeCoverBackgroundColor: PropTypes.string,
  storeDisplayPhoto: PropTypes.object,
  storeName: PropTypes.string,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(CustomizeStoreCoverModal);
